import { Skeleton } from '@/shared/ui/skeleton'

import { PagePending } from './page-pending'

/**
 * Rendered while the session resolves, which on a reload of any protected route
 * is the wait before the route's own.
 *
 * It is the same drawing as the `.boot` shell in `index.html`, in Tailwind
 * instead of inline CSS, so the handover from static HTML to React moves
 * nothing — and a reload never flashes a redirect to login.
 *
 * The geometry is not invented here. It is copied, class for class, from what
 * replaces it: the rail is `Dock`'s `w-21.5` with its capsule at
 * `left-[15px] w-14`, and the column is `AppLayout`'s
 * `max-w-column-max px-page-inline pt-22 pb-8 max-sm:pb-28`. Any of those three
 * files changing its shape has to change all three, or a reload starts jumping
 * again.
 *
 * The column's *contents* are `PagePending` itself rather than a second set of
 * bars, and that is the same rule one level in. A reload runs two waits back to
 * back — the session, then the route — so this placeholder is replaced by that
 * one before any real content appears. Drawing them differently made a reload
 * show a skeleton, then the same skeleton 50px taller, then the page: two
 * jumps where the waits should have looked like one.
 *
 * The floating chrome is deliberately absent. It is `fixed`, so it costs the
 * layout nothing — and it belongs to a signed-in session, which is precisely
 * what has not resolved yet.
 */
export function AppShellSkeleton() {
  return (
    <div className="flex h-full">
      <div className="relative w-21.5 shrink-0 max-sm:hidden">
        {/* Same capsule as the dock: border, glass fill, radius 20. Tiles are
            the dock's `size-10 rounded-xl`, and there are three — a non-admin
            session. An admin's fourth grows from the capsule's own centre and
            moves nothing sideways. */}
        <div className="absolute top-1/2 left-[15px] flex w-14 -translate-y-1/2 flex-col items-center gap-1.5 rounded-[20px] border border-border bg-surface-1 px-2 py-2.5">
          <Skeleton className="size-10 rounded-xl" />
          <Skeleton className="size-10 rounded-xl" />
          <Skeleton className="size-10 rounded-xl" />
        </div>
      </div>

      <main className="min-w-0 flex-1 overflow-auto [scrollbar-gutter:stable]">
        <div className="mx-auto flex max-w-column-max flex-col px-page-inline pt-22 pb-8 max-sm:pb-28">
          <PagePending />
        </div>
      </main>
    </div>
  )
}
